<script lang="ts">
    
    import { onDestroy } from "svelte";
    import { flip } from 'svelte/animate';
    import type { RealTimeEvent } from "../../global";
    import type { Board } from "../../Model/Board";
    import type { Game } from "../../Model/Game";
    import type { Player } from "../../Model/Player";
    import { boardsFactory, boardsService, eventsChannelsFactory, gamesFactory, playersFactory, roomsService } from "../../Services";
    import type { EventsChannel } from "../../Services/EventsChannel";
    import AspectRatio from "../Shared/AspectRatio.svelte";
    import Picture from "../Shared/Picture.svelte";
    import Watch from "../Watch.svelte";

    type PlayerItem = {
        player: Player;
        board?: Board;
    };

    export let roomId: string;
    export let maxPlayers: number;

    let game: Game;
    let playerItems: PlayerItem[] = [];
    let roomEvents: EventsChannel;
    let containerWidth = 200;
    let refreshInterval;

    onDestroy(() => {
        roomEvents?.destroy();
        clearInterval(refreshInterval); // Limpiar el intervalo
    });

    $: getPlayers(roomId);

    async function getPlayers(roomId: string): Promise<void> {
        roomEvents?.destroy();
        
        game = await roomsService.getCurrentGameAtRoom(roomId);
        const players = await roomsService.getPlayersInRoomAndGame(roomId, game.id);

        playerItems = players.map(player => ({ player }));
        refreshBoards();
        roomEvents = eventsChannelsFactory.createEventsChannel(`room.${roomId}`);
        roomEvents.on('game_created', onGameCreated);
        roomEvents.on('player_enter', onPlayerEnter);
        // roomEvents.on('player_leave', onPlayerLeave);
        roomEvents.on('board_created', onBoardCreated);
        roomEvents.on('number_checked', onNumberChecked);
        roomEvents.on('identity_override', onIdentityOverride);

        setupAutoRefresh(); // Configurar el refresco automático
    }

    function setupAutoRefresh(): void {
        clearInterval(refreshInterval); // Limpiar cualquier intervalo existente
        refreshInterval = setInterval(() => {
            if (hasPlayersWithoutBoards()) {
                refreshBoards();
            }
        }, 30000); // 30 segundos
    }

    async function refreshBoards(): Promise<void> {
        const boards = await boardsService.getBoardsOfCurrentGameInRoom(roomId);
        for (const playerItem of playerItems) {
            playerItem.board = boards.find(({ playerId }) => playerId === playerItem.player.id);
        }
        sortPlayerItems();
        playerItems = playerItems;
    }

    function hasPlayersWithoutBoards(): boolean {
        return playerItems.some(playerItem => !playerItem.board);
    }

    function hasPlayer(playerId: string): boolean {
        return playerItems.some(({ player }) => player.id === playerId)
    }

    function onGameCreated({ data }: RealTimeEvent): void {
        game = gamesFactory.createGameFromDto(data);
    }

    function onPlayerEnter({ data }: RealTimeEvent): void {
        if (hasPlayer(data.player.id)) {
            let updated = [];
            playerItems.forEach((info) => {
                if(info.player.id === data.player.id) {
                    info.player = playersFactory.createPlayerFromDto(data.player);
                }
                updated.push(info);
            });
            playerItems = updated;
            return;
        }
        const playerItem: PlayerItem = {
            player: playersFactory.createPlayerFromDto(data.player),
        };
        playerItems = [...playerItems, playerItem];
        sortPlayerItems();

        const playerSearch = playerItems.find((p: any) => p.player.id === data.player.id);

        if (!game || playerSearch) {
            console.log('No hay juego o el Jugador ya existe');
            return;
        }
    }

    function onPlayerLeave({ data }: RealTimeEvent): void {
        if (!hasPlayer(data.player.id)) {
            return;
        }
    }

    function onBoardCreated({ data }: RealTimeEvent): void {
        const playerItem = playerItems.find(({ player }) => player.id === data.board.playerId);
        if (!playerItem) {
            return;
        }
        playerItem.board = boardsFactory.createBoardFromDto(data.board);
        sortPlayerItems();
        playerItems = playerItems;
    }

    function sortPlayerItems(): void {
        playerItems.sort((a, b) => {
            if (a.board?.checkedCount && b.board?.checkedCount) {
                return b.board.checkedCount.value - a.board.checkedCount.value;
            }
            if (!a.board?.checkedCount) {
                return 1;
            }
            if (!b.board?.checkedCount) {
                return -1;
            }
            return 0;
        });
    }

    function onNumberChecked({ data }: RealTimeEvent): void {
        const playerItem = playerItems.find(({ board }) => board?.id === data.boardId);
        if (!playerItem) {
            return;
        }
        playerItem.board.checkCell(data.number);
        sortPlayerItems();
        playerItems = playerItems;
    }

    function onIdentityOverride({ data }: RealTimeEvent): void {
        const playerItem = playerItems.find(({ player }) => player.id === data.player.id);
        if (!playerItem) {
            return;
        }
        playerItem.player.name.set(data.player.name);
        playerItem.player.pictureId.set(data.player.pictureId);
        playerItem.player.identityOverride.set(true);
    }

</script>

<div class="players-ranking notranslate" bind:clientWidth={containerWidth}>
    {#each playerItems.slice(0, maxPlayers) as playerItem (playerItem)}
        <div class="player" animate:flip>
            <div class="picture">
                {#if playerItem.player.pictureId}
                    <AspectRatio>
                        <Picture id={playerItem.player.pictureId} />
                    </AspectRatio>
                {:else}
                    <AspectRatio>
                        <img src={'/images/default-placeholder.png'} alt="">
                    </AspectRatio>
                {/if}
            </div>
            <div class="info">
                <div class="name" style="font-size: {containerWidth * 0.005}rem; padding-top: {containerWidth * 0.007}px;">
                    <Watch state={playerItem.player.name} />
                </div>
                {#if playerItem.board}
                    <div class="checked-count" style="font-size: {containerWidth * 0.005}rem;">
                        <Watch state={playerItem.board.checkedCount} />/{playerItem.board.numCells}
                    </div>
                {/if}
            </div>
        </div>
    {/each}
</div>

<style lang="scss">

    .player {
        width: 100%;
        display: flex;
        align-items: center;

        & + .player {
            margin-top: 0.5rem;
        }
    }

    .picture {
        position: relative;
        z-index: 2;
        width: 6rem;
        border-radius: 50%;
        overflow: hidden;
        border: 5px solid #fff;
        background-color: #d8d8d8;

        :global(img) {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .info {
        flex: 1 1 0;
        box-sizing: border-box;
        margin-left: -3rem;
        padding-left: 4rem;
        padding-right: 1rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: rgba(255, 255, 255, 0.5);
        border-top-right-radius: 1rem;
        border-bottom-right-radius: 1rem;
    }

    .name {
        font-family: ManagerTitle;
        padding-top: 8px;
        box-sizing: border-box;
        max-width: 12vw;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .checked-count {
        font-family: ManagerTitle;
        white-space: nowrap;
    }

</style>